import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import {
  Plus, CheckCircle2, Circle, Clock, X,
} from 'lucide-react';

import HierarchyBreadcrumb from '../components/HierarchyBreadcrumb';
import HoursProgressBar from '../components/HoursProgressBar';
import DeadlineBanner from '../components/DeadlineBanner';
import DeadlineBadge from '../components/DeadlineBadge';
import { getStatusLabel } from '../types';
import type { Ticket, TicketPriority } from '../types';

// ============================================================
// CreateTaskModal
// ============================================================
function CreateTaskModal({ milestoneId, projectId, onClose }: {
  milestoneId: string;
  projectId: string;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [estimatedHours, setEstimatedHours] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medium');

  // We need to find a parent ticket to create subtask or create a new ticket under milestone
  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.getUsers({ limit: 100 }),
  });
  const [assignedAgentId, setAssignedAgentId] = useState('');
  const agents = (usersData?.data ?? []).filter((u) => u.role === 'admin' || u.role === 'agent');

  const currentUser = useAuthStore((state) => state.user);

  const mutation = useMutation({
    mutationFn: () => {
      return api.createTicket({
        customer_id: currentUser!.id,
        title,
        description: description || undefined,
        priority,
        assigned_agent_id: assignedAgentId || undefined,
      } as any).then(async (res) => {
        if (res.data?.id) {
          await api.moveTicket(res.data.id, { project_id: projectId, milestone_id: milestoneId });
          if (estimatedHours) {
            await api.updateTicket(res.data.id, { estimated_hours: parseFloat(estimatedHours) } as any);
          }
        }
        return res;
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestone-tickets'] });
      queryClient.invalidateQueries({ queryKey: ['project'] });
      onClose();
    },
  });

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-md w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-surface-900">{t('milestone.newTask')}</h2>
          <button onClick={onClose} className="text-surface-400 hover:text-surface-600"><X size={24} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate(); }} className="space-y-4">
          <div>
            <label className="label">{t('milestone.taskTitle')}</label>
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} required minLength={3} />
          </div>
          <div>
            <label className="label">{t('milestone.taskDesc')}</label>
            <textarea className="input min-h-[60px]" value={description} onChange={(e) => setDescription(e.target.value)} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">{t('milestone.taskEstHours')}</label>
              <input type="number" step="0.5" className="input" value={estimatedHours} onChange={(e) => setEstimatedHours(e.target.value)} />
            </div>
            <div>
              <label className="label">{t('milestone.taskPriority')}</label>
              <select className="input" value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
                <option value="low">{t('milestone.priorityLow')}</option>
                <option value="medium">{t('milestone.priorityMedium')}</option>
                <option value="high">{t('milestone.priorityHigh')}</option>
                <option value="urgent">{t('milestone.priorityUrgent')}</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">{t('milestone.taskAgent')}</label>
            <select className="input" value={assignedAgentId} onChange={(e) => setAssignedAgentId(e.target.value)}>
              <option value="">{t('common.unassigned')}</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </div>
          {mutation.error && <p className="text-sm text-danger-600">{t('milestone.createTaskError')}</p>}
          <div className="flex gap-3">
            <button type="submit" disabled={mutation.isPending} className="btn btn-primary flex-1">
              {mutation.isPending ? t('common.creating') : t('milestone.createTask')}
            </button>
            <button type="button" onClick={onClose} className="btn btn-secondary flex-1">{t('common.cancel')}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================
// MilestonePage
// ============================================================
export default function MilestonePage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const user = useAuthStore((s) => s.user);
  const [showCreateTask, setShowCreateTask] = useState(false);

  const { data: milestoneData, isLoading: milestoneLoading } = useQuery({
    queryKey: ['milestone', id],
    queryFn: () => api.getMilestone(id!),
    enabled: !!id,
  });

  const { data: ticketsData, isLoading: ticketsLoading } = useQuery({
    queryKey: ['milestone-tickets', id],
    queryFn: () => api.getMilestoneTickets(id!, { limit: 100 }),
    enabled: !!id,
  });

  const milestone = milestoneData?.data;
  const tickets: Ticket[] = ticketsData?.data ?? [];
  const isAdmin = user?.role === 'admin';
  const isAgentOrAdmin = user && ['admin', 'agent'].includes(user.role);
  const queryClient = useQueryClient();

  const completeMutation = useMutation({
    mutationFn: () => api.completeMilestone(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['milestone', id] });
      queryClient.invalidateQueries({ queryKey: ['milestone-tickets', id] });
    },
  });

  const totalEstimated = tickets.reduce((s, t) => s + (t.estimated_hours || 0), 0);
  const totalActual = tickets.reduce((s, t) => s + (t.actual_hours || 0), 0);
  const closedCount = tickets.filter((t) => t.status === 'closed').length;

  const breadcrumbItems = [
    ...(milestone?.organization_name ? [{ label: milestone.organization_name, href: `/organizations/${milestone.organization_id}` }] : []),
    ...(milestone?.project_name ? [{ label: milestone.project_name, href: `/projects/${milestone.project_id}` }] : []),
    { label: milestone?.name || t('milestone.milestoneLabel') },
  ];

  if (ticketsLoading || milestoneLoading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <HierarchyBreadcrumb items={breadcrumbItems} />

      {/* Deadline banner */}
      <DeadlineBanner
        dueDate={milestone?.due_date}
        source="milestone"
        warningDays={milestone?.deadline_warning_days || 7}
        entityLabel={t('milestone.theMilestone')}
      />

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">{milestone?.name || t('milestone.milestoneLabel')}</h1>
          <p className="text-sm text-surface-500 mt-1">
            {closedCount}/{tickets.length}{t('milestone.tasksCompleted')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && milestone?.status !== 'completed' && (
            <button
              onClick={() => completeMutation.mutate()}
              disabled={completeMutation.isPending}
              className="btn btn-secondary flex items-center gap-2 text-sm"
              aria-label={t('milestone.markComplete')}
            >
              <CheckCircle2 size={16} /> {t('milestone.markComplete')}
            </button>
          )}
          {isAgentOrAdmin && (
            <button onClick={() => setShowCreateTask(true)}
              className="btn btn-primary flex items-center gap-2 text-sm">
              <Plus size={16} /> {t('milestone.newTask')}
            </button>
          )}
        </div>
      </div>

      {/* Progress */}
      {totalEstimated > 0 && (
        <HoursProgressBar actual={totalActual} estimated={totalEstimated} className="mb-2" />
      )}

      {/* Task progress bar */}
      {tickets.length > 0 && (
        <div className="card">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-surface-700">{t('milestone.taskProgress')}</span>
            <span className="text-sm font-bold text-surface-800">
              {tickets.length > 0 ? Math.round((closedCount / tickets.length) * 100) : 0}%
            </span>
          </div>
          <div className="w-full bg-surface-200 rounded-full h-2">
            <div className="bg-primary-500 h-2 rounded-full transition-all"
              style={{ width: `${tickets.length > 0 ? (closedCount / tickets.length) * 100 : 0}%` }} />
          </div>
        </div>
      )}

      {/* Task list */}
      <div className="card">
        <h2 className="text-base font-bold text-surface-800 mb-4">{t('milestone.tasks')}</h2>
        {tickets.length === 0 ? (
          <p className="text-sm text-surface-400 text-center py-6">{t('milestone.noTasks')}</p>
        ) : (
          <div className="space-y-2">
            {tickets.map((ticket) => (
              <Link key={ticket.id} to={`/tickets/${ticket.id}`}
                className="flex items-center justify-between p-3 rounded-lg border border-surface-400/10 hover:border-primary-200 hover:bg-primary-50/30 transition-colors">
                <div className="flex items-center gap-3 min-w-0">
                  {ticket.status === 'closed' ? (
                    <CheckCircle2 size={18} className="text-success-500 flex-shrink-0" />
                  ) : ticket.status === 'in_progress' ? (
                    <Clock size={18} className="text-info-500 flex-shrink-0" />
                  ) : (
                    <Circle size={18} className="text-surface-400 flex-shrink-0" />
                  )}
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${ticket.status === 'closed' ? 'text-surface-400 line-through' : 'text-surface-800'}`}>
                      {ticket.title}
                    </p>
                    <p className="text-xs text-surface-400">
                      {ticket.agent_name || t('common.unassigned')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0">
                  {ticket.due_date && <DeadlineBadge dueDate={ticket.due_date} />}
                  {ticket.estimated_hours != null && (
                    <span className="text-xs text-surface-500">
                      {ticket.actual_hours}/{ticket.estimated_hours} {t('common.hoursShort')}
                    </span>
                  )}
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    ticket.status === 'open' ? 'bg-info-100 text-info-700' :
                    ticket.status === 'in_progress' ? 'bg-warning-100 text-warning-700' :
                    ticket.status === 'waiting' ? 'bg-orange-100 text-orange-700' :
                    'bg-success-100 text-success-700'
                  }`}>
                    {getStatusLabel(ticket.status, t)}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>

      {showCreateTask && milestone?.project_id && (
        <CreateTaskModal
          milestoneId={id!}
          projectId={milestone.project_id}
          onClose={() => setShowCreateTask(false)}
        />
      )}
    </div>
  );
}
